import React, { useState, useEffect } from 'react';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Dropdown, DropdownItem } from 'shared/components';
import LastPageIcon from 'assets/svgs/lastPageArrow.svg';
import FirstPageIcon from 'assets/svgs/firstPageArrow.svg';
import NextPageIcon from 'assets/svgs/nextArrow.svg';
import PrevPageIcon from 'assets/svgs/prevArrow.svg';
import DropdownArrow from 'assets/svgs/dropdownArrow.svg';
import ArrowDownIcon from 'assets/svgs/arrowDownIcon.svg';
import './Pagination.scss';

const Pagination = ({
  children,
  showNumbersList,
  showCurrentPage,
  displayHeader,
  pageSizeOptions,
  pageOptionsLabel,
  showSelectPage,
  ofLabel,
  openRemoveModal,
  itemsTotalCount,
  examineeList,
  initialPage,
  pageSize,
  onChangePage,
  onSelectAll,
  temporarySelectedExaminees,
  onDeselectAllHandle,
  openAbsentModal,
  openDeniedModal,
}) => {
  const { t } = useTranslation();

  const [pager, setPager] = useState({});

  const setPage = (page, pageSizeNew) => {
    if (page < 1 || page > pager.totalPages) {
      return;
    }
    const newPager = getPager(itemsTotalCount, page, pageSizeNew || pageSize);
    setPager(newPager);
    onChangePage(newPager.startIndex, newPager.endIndex, pageSizeNew || newPager.pageSize);
  };

  const getPager = (totalItems, currentPage, pageSize) => {
    currentPage = currentPage || 1;
    pageSize = pageSize || 10;

    let totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;

    const startPage = 1;

    let startIndex = (currentPage - 1) * pageSize;
    let endIndex = Math.min(startIndex + pageSize - 1, totalItems - 1);

    let pages = [...Array(totalPages).keys()].map((i) => startPage + i);
    return {
      totalItems: totalItems,
      currentPage: currentPage,
      pageSize: pageSize,
      totalPages: totalPages,
      startPage: startPage,
      startIndex: startIndex,
      endIndex: endIndex,
      pages: pages,
    };
  };

  const onChangePageSize = (value) => {
    setPage(initialPage, value);
  };

  useEffect(() => {
    if (itemsTotalCount > 0) {
      setPage(initialPage);
    }
  }, []);

  useEffect(() => {
    setPage(initialPage);
  }, [itemsTotalCount]);

  const width = window.innerWidth;
  const isMobile = width < 768;
  const isTablet = width >= 768 && width < 1200;
  const isDesktop = width >= 1200;

  return (
    <>
      <div className="counter-size-container">
        <Counter
          totalCount={itemsTotalCount}
          end={pager.endIndex + 1}
          start={pager.startIndex + 1}
          resultText={ofLabel}
          isMobile={isMobile}
          pager={pager}
        />
        {pageSizeOptions && pageSizeOptions.length && (isMobile || isTablet) && !isEmpty(pager) && (
          <PageSizeSelect
            pageSizeOptions={pageSizeOptions}
            pager={pager}
            onChangePageSize={onChangePageSize}
            pageOptionsLabel={pageOptionsLabel}
            className="page-size-select-container"
          />
        )}
      </div>
      {isMobile && (
        <div className="pagination-settings">
          {pageSizeOptions && pageSizeOptions.length && isDesktop && !isEmpty(pager) && (
            <PageSizeSelect
              pageSizeOptions={pageSizeOptions}
              pager={pager}
              onChangePageSize={onChangePageSize}
              pageOptionsLabel={pageOptionsLabel}
              className="page-size-select-container"
            />
          )}
          {!isEmpty(pager) && (
            <PageSelection
              showNumbersList={showNumbersList}
              showCurrentPage={showCurrentPage}
              showSelectPage={showSelectPage}
              pager={pager}
              setPage={setPage}
            />
          )}
        </div>
      )}
      <div className="pagination-container">
        {displayHeader && (
          <div className="pagination-header">
            <div className="interaction-bar">
              <div className="bulk-actions-container">
                <div
                  className={`selected-number ${
                    temporarySelectedExaminees.length > 0 && 'selected'
                  }`}>
                  {temporarySelectedExaminees.length}
                </div>
                <div className="selected-label">selected</div>
                <div className="dropdown-icon-container">
                  <Dropdown
                    customClass="select-item"
                    isDropdownAvailable={examineeList.length > 0}
                    chevron={<DropdownArrow />}>
                    <DropdownItem
                      handleClick={
                        temporarySelectedExaminees.length > 0 ? onDeselectAllHandle : onSelectAll
                      }
                      text={
                        temporarySelectedExaminees.length > 0
                          ? t('common.labels.deselectAll')
                          : t('common.labels.selectAll')
                      }
                    />
                  </Dropdown>
                </div>
                <div className="bulk-actions-buttons-container">
                  <button
                    disabled={!temporarySelectedExaminees.length}
                    className={`bulk-actions-button ${
                      temporarySelectedExaminees.length > 0 && 'active'
                    }`}
                    onClick={openRemoveModal}>
                    {t('roomRoster.removeFromRoomButton')}
                  </button>
                  <Dropdown
                    customClass={`select-item red ${
                      temporarySelectedExaminees.length > 0 && 'active'
                    }`}
                    isDropdownAvailable={temporarySelectedExaminees.length > 0}
                    chevron={<ArrowDownIcon />}>
                    <DropdownItem
                      handleClick={openAbsentModal}
                      text={t('roomRoster.markAsAbsent')}
                      key="1"
                    />
                    <DropdownItem
                      handleClick={openDeniedModal}
                      text={t('roomRoster.markAsDenied')}
                      key="2"
                    />
                  </Dropdown>
                </div>
              </div>
            </div>
            {(isDesktop || isTablet) && (
              <div className="pagination-settings">
                {pageSizeOptions && pageSizeOptions.length && isDesktop && !isEmpty(pager) && (
                  <PageSizeSelect
                    pageSizeOptions={pageSizeOptions}
                    pager={pager}
                    onChangePageSize={onChangePageSize}
                    pageOptionsLabel={pageOptionsLabel}
                    className="page-size-select-container"
                  />
                )}
                {!isEmpty(pager) && (
                  <PageSelection
                    showNumbersList={showNumbersList}
                    showCurrentPage={showCurrentPage}
                    showSelectPage={showSelectPage}
                    pager={pager}
                    setPage={setPage}
                  />
                )}
              </div>
            )}
          </div>
        )}
        <div className="pagination-body">{children}</div>
      </div>
    </>
  );
};

const Counter = ({ totalCount, end, start, isMobile, pager }) => {
  const { t } = useTranslation();

  if (totalCount === end && pager.totalPages === 1) {
    return (
      <p className="counter-results">
        <strong>{`${totalCount} `}</strong>
        {totalCount === 1 ? t('rosters.examineeOfLabel') : t('rosters.examineesOfLabel')}
      </p>
    );
  }
  return (
    <p className="counter-results">
      {totalCount > 0 ? (
        <span>
          <strong>
            {start}-{end}
          </strong>{' '}
          {t('common.labels.outOf')} <strong>{totalCount} </strong>{' '}
          {isMobile
            ? t('rosters.results')
            : totalCount === 1
            ? t('rosters.examineeOfLabel')
            : t('rosters.examineesOfLabel')}
        </span>
      ) : (
        <span>
          <strong>0 </strong>
          {t('common.labels.zeroResults')}
        </span>
      )}
    </p>
  );
};

const PageSelection = (props) => (
  <div className="pagination">
    <button
      className={props.pager.currentPage === 1 ? 'disabled' : ''}
      tabIndex="0"
      onClick={() => props.setPage(1)}>
      <FirstPageIcon />
    </button>
    <button
      className={props.pager.currentPage === 1 ? 'disabled' : ''}
      tabIndex="0"
      onClick={() => props.setPage(props.pager.currentPage - 1)}>
      <PrevPageIcon />
    </button>
    {props.showNumbersList &&
      props.pager.pages.map((page, index) => (
        <span
          key={index}
          className={props.pager.currentPage === page ? 'active' : ''}
          tabIndex="0"
          onClick={() => props.setPage(page)}>
          <span>
            Page {props.pager.currentPage}/{props.pager.totalPages}
          </span>
        </span>
      ))}
    {props.showCurrentPage && (
      <span>
        Page {props.pager.currentPage}/{props.pager.totalPages}
      </span>
    )}
    {props.showSelectPage && (
      <Dropdown
        customClass="select-page"
        title={`Page ${props.pager.currentPage}/${props.pager.totalPages}`}
        name="page-select"
        isDropdownAvailable={props.pager.pages && props.pager.pages.length > 1}
        chevron={props.pager.totalPages > 1 ? <DropdownArrow /> : null}>
        {props.pager.pages.map((page, index) => (
          <DropdownItem
            tabIndex="0"
            key={index}
            handleClick={() => props.setPage(page)}
            text={`${page}/${props.pager.totalPages}`}
          />
        ))}
      </Dropdown>
    )}
    <button
      tabIndex="0"
      className={props.pager.currentPage === props.pager.totalPages ? 'disabled' : ''}
      onClick={() => props.setPage(props.pager.currentPage + 1)}>
      <NextPageIcon />
    </button>
    <button
      tabIndex="0"
      className={props.pager.currentPage === props.pager.totalPages ? 'disabled' : ''}
      onClick={() => props.setPage(props.pager.totalPages)}>
      <LastPageIcon />
    </button>
  </div>
);

const PageSizeSelect = (props) => {
  const { t } = useTranslation();
  return (
    <div className={props.className}>
      {props.pageOptionsLabel && <span className="pe-2">{props.pageOptionsLabel}</span>}

      <Dropdown
        customClass="select-page-size"
        title={props.pager.pageSize}
        name="page-select"
        isDropdownAvailable={true}
        chevron={<DropdownArrow />}>
        {props.pageSizeOptions.map((element, index) => (
          <DropdownItem
            tabIndex="0"
            key={index}
            handleClick={() => props.onChangePageSize(element)}
            text={element}
          />
        ))}
      </Dropdown>
      <span>{t('common.labels.perPage')}</span>
    </div>
  );
};

Pagination.defaultProps = {
  initialPage: 1,
  pageSize: 10,
  showNumbersList: false,
  showCurrentPage: false,
  showSelectPage: false,
  displayHeader: true,
  pageOptionsLabel: '',
  paginationDataParams: {},
};

export default Pagination;
